var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState, useEffect, } from "react";
import { createUseStyles } from "react-jss";
import { BodyText } from "./BodyText";
import { BASE_BUTTON } from "./Button/Button.styles";
import { useLocalStorage } from "./hooks/useLocalStorage";
import { useTranslation } from "./hooks/useTranslation";
import { THEME, TMS_THEME } from "./theming/theming";
import { Mode } from "./types";
import { classNames } from "./utils/classNames";
var DISPLAY_DURATION_IN_MILLISECONDS = 4000;
export var Alert = function (_a) {
    var message = _a.message, glyph = _a.glyph, _b = _a.type, type = _b === void 0 ? "warning" : _b, _c = _a.overPage, overPage = _c === void 0 ? false : _c, className = _a.className, id = _a.id, onDismiss = _a.onDismiss, _d = _a.dismissButtonType, dismissButtonType = _d === void 0 ? "simple" : _d, dataId = _a.dataId, dataTestId = _a.dataTestId, permanentlyDismiss = _a.permanentlyDismiss;
    var _e = useState(true), visible = _e[0], setVisible = _e[1];
    var classes = useAlertClasses(type);
    var _f = useLocalStorage(getAlertStorageKey(permanentlyDismiss === null || permanentlyDismiss === void 0 ? void 0 : permanentlyDismiss.client, permanentlyDismiss === null || permanentlyDismiss === void 0 ? void 0 : permanentlyDismiss.name), null), dismissed = _f[0], setDismissed = _f[1];
    useEffect(function () {
        if (overPage) {
            setVisible(true);
            var hideOverPageAlert_1 = setTimeout(function () { return setVisible(false); }, DISPLAY_DURATION_IN_MILLISECONDS);
            return function () { return clearTimeout(hideOverPageAlert_1); };
        }
    }, [overPage, message]);
    if (permanentlyDismiss && dismissed)
        return null;
    var handleDismiss = function () {
        setVisible(false);
        onDismiss && onDismiss();
        if (permanentlyDismiss) {
            setDismissed(new Date().toISOString());
        }
    };
    var glyphToDisplay = glyph || getGlyphForAlertType(type);
    var alert = (React.createElement(AlertWrapper, { type: type, className: className, id: id, dataId: dataId, dataTestId: dataTestId, overPage: overPage },
        React.createElement(BodyText, { glyph: glyphToDisplay, message: message, mode: type === "warning" ? Mode.Light : Mode.Dark }),
        (onDismiss || permanentlyDismiss) && (React.createElement(DismissButton, { id: id ? "".concat(id, "--close") : undefined, buttonType: dismissButtonType, onClose: function () { return handleDismiss(); }, type: type }))));
    if (!visible)
        return null;
    if (overPage)
        return (React.createElement("div", { className: classNames("over-page", classes.overPageWrapper) }, alert));
    return alert;
};
var AlertWrapper = function (_a) {
    var type = _a.type, children = _a.children, className = _a.className, id = _a.id, dataId = _a.dataId, dataTestId = _a.dataTestId, overPage = _a.overPage;
    var classes = useAlertClasses(type);
    return (React.createElement("div", { role: "alert", className: classNames(classes.alert, overPage ? classes.overPageAlert : "", "alert", "alert-".concat(type), className !== null && className !== void 0 ? className : ""), id: id, "data-testid": dataTestId, "data-id": dataId }, children));
};
var DismissButton = function (_a) {
    var id = _a.id, onClose = _a.onClose, buttonType = _a.buttonType, type = _a.type;
    var t = useTranslation();
    var classes = useAlertClasses(type);
    return buttonType === "simple" ? (React.createElement("button", { type: "button", className: classNames("close", classes.close), "data-dismiss": "modal", "aria-label": t("Dismiss"), onClick: onClose, id: id, "data-testid": id, "data-hook": "dismiss-button" },
        React.createElement("span", { "aria-hidden": "true" }, "\u00D7"))) : (React.createElement("span", { className: classes.closeWrapper },
        React.createElement("button", { type: "button", id: id, "data-dismiss": "modal", onClick: onClose, "data-testid": id, "data-hook": "dismiss-button", className: classes.closeButton }, t("Dismiss"))));
};
export var getGlyphForAlertType = function (type) {
    if (type === "success") {
        return "circleCheck";
    }
    if (type === "info") {
        return "infoSign";
    }
    if (type === "warning") {
        return "exclamationSign";
    }
    if (type === "error") {
        return "circleAlert";
    }
    return undefined;
};
export var getAlertStorageKey = function (client, name) {
    return "".concat(client, ":permanentlyDismissableAlert:").concat(name, ":dismissed");
};
var useAlertClasses = function (type) {
    var closeButtonColor = "#fff";
    var alert = {
        margin: [0, 0, 16, 0],
        padding: 16,
        border: [1, "solid", "transparent"],
        borderRadius: 4,
        display: "flex",
        backgroundColor: THEME.palette.primary.base.color,
        color: THEME.palette.primary.base.contrast,
        alignItems: "center",
        "& > .body-text": {
            flexGrow: 1,
        },
        "& ul": {
            paddingLeft: 16,
            marginBottom: 0,
        },
    };
    if (type === "error") {
        alert = __assign(__assign({}, alert), { backgroundColor: THEME.palette.error.base.color, color: THEME.palette.error.base.contrast });
    }
    if (type === "success") {
        alert = __assign(__assign({}, alert), { backgroundColor: THEME.palette.success.base.color, color: THEME.palette.success.base.contrast });
    }
    if (type === "warning") {
        alert = __assign(__assign({}, alert), { backgroundColor: THEME.palette.warning.base.color, color: THEME.palette.warning.base.contrast });
        closeButtonColor = THEME.palette.warning.base.contrast;
    }
    var useStyles = createUseStyles({
        alert: alert,
        overPageWrapper: {
            position: "fixed",
            top: 0,
            left: "40%",
            zIndex: 9999,
        },
        overPageAlert: {
            position: "relative",
            minWidth: 200,
            margin: [30, "auto", 0],
        },
        close: {
            color: closeButtonColor,
            textShadow: "unset",
            opacity: 1,
            marginLeft: 10,
            marginTop: -5,
            "&:hover": {
                color: closeButtonColor,
                opacity: 0.7,
            },
        },
        closeButton: __assign(__assign({ backgroundColor: "transparent", border: 0, color: closeButtonColor, padding: [5, 10] }, BASE_BUTTON), { "&:hover": {
                color: closeButtonColor,
                opacity: 0.8,
            } }),
        closeWrapper: {
            display: "flex",
            borderLeft: [
                type === "warning" ? TMS_THEME.palette.gray.slate.color : "#fff",
                1,
                "solid",
            ],
            paddingLeft: 15,
        },
    });
    return useStyles();
};
